import React from 'react';
import { Device } from '../../lib/types';
import { formatDate } from '../../lib/utils';
import { SkeletonRows } from './SkeletonRow';
import Pagination from '../../components/Pagination/Pagination';
import styles from './Devices.module.scss';

const columns = ['New Owner', 'Pet', 'Device', 'Completed', ''];

interface CompletedTransfersTableProps {
  devices: Device[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  skeletonCount: number;
}

export default function CompletedTransfersTable({
  devices,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  loading,
  skeletonCount,
}: CompletedTransfersTableProps) {
  if (totalCount === 0 && !loading && skeletonCount === 0) {
    return null;
  }

  const hasData = devices.length > 0;
  const showOverlay = skeletonCount > 0 && hasData;
  const showSkeleton = skeletonCount > 0 && !hasData;
  const wrapperClass = `${styles.tableWrapper}${showOverlay ? ` ${styles.tableTransitioning}` : ''}`;

  return (
    <div className={`${styles.sectionCard} ${styles.sectionCardSuccess}`}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Completed Transfers ({totalCount})</h3>
        <p className={styles.sectionDescription}>Devices successfully transferred to adopters.</p>
      </div>
      <div className={wrapperClass}>
        <table className={styles.table}>
          <colgroup>
            <col style={{ width: '28%' }} /> {/* New Owner */}
            <col style={{ width: '18%' }} /> {/* Pet */}
            <col style={{ width: '22%' }} /> {/* Device */}
            <col style={{ width: '22%' }} /> {/* Completed */}
            <col style={{ width: '10%' }} /> {/* Checkmark */}
          </colgroup>
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th key={i}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {showSkeleton ? (
              <SkeletonRows count={skeletonCount} columns={columns.length} />
            ) : (
              devices.map((d) => (
                <tr key={d.id}>
                  <td>
                    {d.adopterName}
                    {d.adopterEmail && (
                      <>
                        <br />
                        <span className={styles.adopterEmail}>{d.adopterEmail}</span>
                      </>
                    )}
                  </td>
                  <td>{d.petName || '\u2014'}</td>
                  <td>
                    <div className={styles.deviceId}>{d.id}</div>
                    <div className={styles.deviceType}>{d.type}</div>
                  </td>
                  <td>{formatDate(d.completedDate)}</td>
                  <td>
                    <span className={styles.checkmark}>&#10003;</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
}
